import { useCallback, useEffect, useRef, useState } from "react";
import type { z } from "zod";
import { ApiError } from "../../shared/api/client";
import type {
  corporateUserOptionSchema,
  lineConfigurationSchema,
  operatorAdminSchema,
  ShiftProductionForm,
  ShiftProductionLineName,
  ShiftFormDocumentSettings,
} from "../../../shared/shift-production-form/contracts";
import { emptyShiftFormPayload } from "./form-payload";
import {
  adminCreateForm,
  createOperator,
  getCorporateUsers,
  getDocumentSettings,
  getLineConfigurations,
  getOperators,
  getShiftForm,
  getShiftFormList,
  grantLateCompletion,
  reopenCompletedForm,
  resetOperatorPin,
  revealOperatorPin,
  saveLineConfiguration,
  saveDocumentSettings as saveDocumentSettingsRequest,
  setOperatorActive,
  transferForm,
} from "./shift-production-form-gateway";

type Operator = z.infer<typeof operatorAdminSchema>;
type CorporateUser = z.infer<typeof corporateUserOptionSchema>;
type LineConfiguration = z.infer<typeof lineConfigurationSchema>;

export function useAdminFormWorkspace() {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [users, setUsers] = useState<CorporateUser[]>([]);
  const [configurations, setConfigurations] = useState<LineConfiguration[]>([]);
  const [documentSettings, setDocumentSettings] = useState<ShiftFormDocumentSettings | null>(null);
  const [documentSettingsNotice, setDocumentSettingsNotice] = useState<string | null>(null);
  const [forms, setForms] = useState<
    Awaited<ReturnType<typeof getShiftFormList>>["items"]
  >([]);
  const [selected, setSelected] = useState<ShiftProductionForm | null>(null);
  const [issuedPin, setIssuedPin] = useState<{
    name: string;
    pin: string;
  } | null>(null);
  const pinRequest = useRef(0);
  const clearPin = useCallback(() => {
    pinRequest.current += 1;
    setIssuedPin(null);
  }, []);
  useEffect(() => {
    const hideWhenBackgrounded = () => { if (document.hidden) clearPin(); };
    window.addEventListener("blur", clearPin);
    document.addEventListener("visibilitychange", hideWhenBackgrounded);
    return () => {
      pinRequest.current += 1;
      window.removeEventListener("blur", clearPin);
      document.removeEventListener("visibilitychange", hideWhenBackgrounded);
    };
  }, [clearPin]);
  useEffect(() => {
    if (!issuedPin) return;
    const timer = window.setTimeout(clearPin, 30_000);
    return () => window.clearTimeout(timer);
  }, [issuedPin, clearPin]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const createKey = useRef(crypto.randomUUID());
  const load = useCallback(async () => {
    clearPin();
    setBusy(true);
    setError(null);
    try {
      const [operatorResponse, userResponse, configResponse, formResponse, settingsResponse] =
        await Promise.all([
          getOperators(),
          getCorporateUsers(),
          getLineConfigurations(),
          getShiftFormList("accessible", { limit: 100 }),
          getDocumentSettings(),
        ]);
      setOperators(operatorResponse.items);
      setUsers(userResponse.items);
      setConfigurations(configResponse.items);
      setForms(formResponse.items);
      setDocumentSettings(settingsResponse.settings);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Yönetim verileri alınamadı.",
      );
    } finally {
      setBusy(false);
    }
  }, [clearPin]);
  useEffect(() => {
    void load();
  }, [load]);
  const run = async (work: () => Promise<void>) => {
    clearPin();
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "İşlem tamamlanamadı.",
      );
    } finally {
      setBusy(false);
    }
  };
  const addOperator = (name: string) =>
    run(async () => {
      const request = pinRequest.current;
      const response = await createOperator(name);
      setOperators((current) =>
        [...current, response.operator].sort((a, b) =>
          a.name.localeCompare(b.name, "tr"),
        ),
      );
      if (request === pinRequest.current) setIssuedPin({ name: response.operator.name, pin: response.pin });
    });
  const resetPin = (operatorId: string) =>
    run(async () => {
      const request = pinRequest.current;
      const displayed = operators.find((operator) => operator.id === operatorId);
      if (!displayed) throw new Error("Operatör kaydı bulunamadı. Listeyi yenileyin.");
      const response = await resetOperatorPin(operatorId, displayed.pinChangedAt);
      setOperators((current) => current.map((item) => item.id === operatorId ? response.operator : item));
      if (request === pinRequest.current) setIssuedPin({ name: response.operator.name, pin: response.pin });
    });
  const showPin = (operator: Operator) =>
    run(async () => {
      const request = pinRequest.current;
      const response = await revealOperatorPin(operator.id);
      if (request === pinRequest.current) setIssuedPin({ name: operator.name, pin: response.pin });
    });
  const toggleOperator = (operator: Operator) =>
    run(async () => {
      const response = await setOperatorActive(operator.id, !operator.active);
      setOperators((current) =>
        current.map((item) =>
          item.id === operator.id ? response.operator : item,
        ),
      );
    });
  const saveConfiguration = (configuration: LineConfiguration) =>
    run(async () => {
      const { lineName, version, ...input } = configuration;
      const response = await saveLineConfiguration(lineName, input, version);
      setConfigurations((current) =>
        current.map((item) =>
          item.lineName === lineName ? response.item : item,
        ),
      );
    });
  const reloadDocumentSettings = () =>
    run(async () => {
      const response = await getDocumentSettings();
      setDocumentSettings(response.settings);
    });
  const saveDocumentSettings = async (
    settings: Pick<
      ShiftFormDocumentSettings,
      "documentNo" | "publicationDate" | "revisionDate" | "revisionNo"
    >,
    expectedVersion: number,
  ) => {
    setBusy(true);
    setError(null);
    setDocumentSettingsNotice(null);
    try {
      const response = await saveDocumentSettingsRequest(settings, expectedVersion);
      setDocumentSettings(response.settings);
      setDocumentSettingsNotice("Doküman bilgileri kaydedildi.");
      return { settings: response.settings, conflict: false };
    } catch (reason) {
      const conflict = reason instanceof ApiError && reason.code === "VERSION_CONFLICT";
      const message = conflict
        ? "Doküman bilgileri başka bir yönetici tarafından değiştirildi. Yazdıklarınız korunuyor; güncel bilgileri yükleyip tekrar kaydedin."
        : reason instanceof Error
          ? reason.message
          : "Doküman bilgileri kaydedilemedi.";
      setError(message);
      return { settings: null, conflict };
    } finally {
      setBusy(false);
    }
  };
  const open = (formId: string) =>
    run(async () => setSelected((await getShiftForm(formId)).form));
  const refreshSelected = (form: ShiftProductionForm) => {
    setSelected(form);
    void load();
  };
  const createHistorical = (
    operatorId: string,
    operatorName: string,
    lineName: ShiftProductionLineName,
    productionDate: string,
    shift: "00_08" | "08_16" | "16_00",
    reason: string,
  ) =>
    run(async () => {
      const payload = {
        ...emptyShiftFormPayload(operatorName),
        lineName,
        productionDate,
        shift,
      };
      const response = await adminCreateForm(
        operatorId,
        reason,
        payload,
        createKey.current,
      );
      createKey.current = crypto.randomUUID();
      refreshSelected(response.form);
    });
  const transfer = (operatorId: string, reason: string) =>
    selected
      ? run(async () =>
          refreshSelected(
            (
              await transferForm(
                selected.id,
                selected.version,
                operatorId,
                reason,
              )
            ).form,
          ),
        )
      : Promise.resolve();
  const grant = (reason: string, expiresAt: string) =>
    selected
      ? run(async () =>
          refreshSelected(
            (
              await grantLateCompletion(
                selected.id,
                selected.version,
                reason,
                new Date(expiresAt).toISOString(),
              )
            ).form,
          ),
        )
      : Promise.resolve();
  const reopen = (reason: string) =>
    selected
      ? run(async () =>
          refreshSelected(
            (await reopenCompletedForm(selected.id, selected.version, reason))
              .form,
          ),
        )
      : Promise.resolve();
  return {
    operators,
    users,
    configurations,
    documentSettings,
    documentSettingsNotice,
    forms,
    selected,
    issuedPin,
    busy,
    error,
    load,
    addOperator,
    resetPin,
    showPin,
    toggleOperator,
    saveConfiguration,
    reloadDocumentSettings,
    saveDocumentSettings,
    open,
    setSelected,
    createHistorical,
    transfer,
    grant,
    reopen,
    clearPin,
  };
}
